import type { ComponentType, ReactNode } from "react";
import { ArrowUpRight, Building2 } from "lucide-react";
import { useSupplier } from "@/hooks/useSupplier";
import { addressLines } from "@/components/AddressFields";
import { Spinner } from "@/components/ui/spinner";

/** Renders nothing when no supplier id is provided. */
export function SupplierCard({
  supplierId,
  onSupplierClick,
  LinkComponent,
}: {
  supplierId: string | null | undefined;
  onSupplierClick?: (supplierId: string) => void;
  /** Routed anchor used when `onSupplierClick` is not provided. Receives `href="/suppliers/{id}"`. */
  LinkComponent?: ComponentType<{ href: string; children: ReactNode; className?: string }>;
}) {
  const { supplier, loading } = useSupplier(supplierId);

  if (!supplierId) return null;

  return (
    <div className="rounded-xl border bg-card/60 p-5 backdrop-blur-sm">
      <p className="mb-3 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        Supplier
      </p>

      {loading && !supplier ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Spinner className="size-4" /> Loading supplier...
        </div>
      ) : !supplier ? (
        <p className="text-sm text-muted-foreground">Supplier not found.</p>
      ) : (
        <div className="space-y-2.5">
          <div className="flex items-center gap-2">
            <Building2 className="size-4 shrink-0 text-muted-foreground" />
            <SupplierNameLink
              supplierId={supplier.id ?? supplierId}
              name={supplier.name}
              onSupplierClick={onSupplierClick}
              LinkComponent={LinkComponent}
            />
          </div>
          <DetailRow label="Email" value={supplier.email} />
          <DetailRow label="Phone" value={supplier.phone} />
          <DetailRow label="Address" value={addressLines(supplier.address).join(", ") || null} />
          <DetailRow label="Payment terms" value={supplier.paymentTermsDefault} />
        </div>
      )}
    </div>
  );
}

function SupplierNameLink({
  supplierId,
  name,
  onSupplierClick,
  LinkComponent,
}: {
  supplierId: string;
  name: string;
  onSupplierClick?: (supplierId: string) => void;
  LinkComponent?: ComponentType<{ href: string; children: ReactNode; className?: string }>;
}) {
  const className =
    "inline-flex items-center gap-1 text-sm font-semibold hover:underline";

  if (onSupplierClick) {
    return (
      <button type="button" onClick={() => onSupplierClick(supplierId)} className={`${className} cursor-pointer`}>
        {name}
        <ArrowUpRight className="size-3.5 text-muted-foreground" />
      </button>
    );
  }
  if (LinkComponent) {
    return (
      <LinkComponent href={`/suppliers/${supplierId}`} className={className}>
        {name}
        <ArrowUpRight className="size-3.5 text-muted-foreground" />
      </LinkComponent>
    );
  }
  return <p className="text-sm font-semibold">{name}</p>;
}

function DetailRow({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null;
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium">{value}</span>
    </div>
  );
}
